import React from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import JSDB, {NKTypeConvertor} from '../../../../fromKotlin/nk';
import {roundDecimals} from '../../../../fromKotlin/nkutils';
import {createDriverLink, PERM} from '../../../../api/customer/externalLinks.api';
import {getKartonNum, OrderData} from '../../../../types/OrderData';
import {ProductNote} from '../../../../types/DocumentsTypes/Document';
import {getOrderExtraNoteFields} from '../orderNoteFields';
import {
    BoxSource,
    createDriverLinkRequestPayload,
    dateKey,
    extractDeliveryDate,
    formatIsraelExpiry,
    groupRowsByDeliveryDate,
    parseNumericNote,
    resolveAutomaticBoxes,
    selectLatestLeadingDocument,
} from './driverLinkHelpers';

const DEFAULT_PERMISSIONS = PERM.SEE | PERM.DONE | PERM.ROUTE;

const PERMISSION_OPTIONS = [
    {bit: PERM.DONE, label: 'סימון בוצע והערות'},
    {bit: PERM.ROUTE, label: 'מיטוב ושינוי סדר המסלול'},
    {bit: PERM.EDIT, label: 'עריכת השדות והכמויות במסמך המוביל'},
    {bit: PERM.PRICES, label: 'צפייה במחירים'},
    {bit: PERM.SUMS, label: 'סיכום העמסה וכמויות כוללות'},
] as const;

interface DialogRouteRow {
    row: OrderData;
    orderId: string;
    date: string | null;
    address: string;
    driver: string;
    sourceDocument?: ProductNote;
    sourceType: 'invoice' | 'delivery_certificate' | 'order';
    sourceNumber: string;
    sourceDate: string | null;
    noteValues: Record<string, unknown>;
    automaticBoxes: number | null;
    automaticWarning?: string;
    checked: boolean;
    boxSource: BoxSource;
    sourceOverride: boolean;
    boxes: number | null;
    warning?: string;
}

export interface DriverLinkDialogProps {
    open: boolean;
    rows: OrderData[];
    onClose: () => void;
}

function sourceTypeLabel(type: DialogRouteRow['sourceType']): string {
    if (type === 'invoice') return 'חשבונית';
    if (type === 'delivery_certificate') return 'תעודת משלוח';
    return 'הזמנה';
}

function formatDate(value: string | null): string {
    if (!value) return 'ללא תאריך';
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
}

function getAddress(row: OrderData): string {
    const forced = String(row?.forced_address ?? '').trim();
    if (forced) return forced;
    const client = row?.id ? JSDB().getClient(Number(row.id)).first : undefined;
    const city = client?.city ?? row?.city ?? '';
    const street = client?.street ?? '';
    const streetNumber = client?.streetNumber ?? '';
    const streetPart = [street, streetNumber].filter(Boolean).join(' ');
    return [city, streetPart].filter(Boolean).join(', ');
}

function estimateDocumentBoxes(document?: ProductNote): number | null {
    if (!document?.products?.length) return null;
    try {
        const deliveries = document.products.map((product) => product?.nkObject).filter(Boolean);
        if (!deliveries.length) return null;
        const value = Number(roundDecimals(JSDB().kartonEstimate(NKTypeConvertor().toKotlinList(deliveries))));
        return Number.isFinite(value) && value >= 0 ? value : null;
    } catch {
        return null;
    }
}

function estimateOrderBoxes(row: OrderData): number | null {
    try {
        const value = Number(getKartonNum(row));
        return Number.isFinite(value) && value >= 0 ? value : null;
    } catch {
        return null;
    }
}

function initializeRows(rows: OrderData[]): DialogRouteRow[] {
    const seen = new Set<string>();
    return rows.reduce<DialogRouteRow[]>((result, row) => {
        const orderId = String(row?.orderId ?? '').trim();
        if (!orderId || !Number.isFinite(Number(orderId)) || Number(orderId) <= 0 || seen.has(orderId)) return result;
        seen.add(orderId);

        const sourceDocument = selectLatestLeadingDocument(row.dvs);
        const orderEstimate = estimateOrderBoxes(row);
        const automatic = sourceDocument
            ? resolveAutomaticBoxes(estimateDocumentBoxes(sourceDocument), orderEstimate)
            : resolveAutomaticBoxes(orderEstimate, null);
        const sourceType = sourceDocument?.type === 'invoice'
            ? 'invoice'
            : sourceDocument?.type === 'delivery_certificate'
                ? 'delivery_certificate'
                : 'order';
        const sourceDate = sourceDocument
            ? dateKey(sourceDocument.issueDate) ?? dateKey(sourceDocument.date)
            : dateKey(row.date);
        const noteValues: Record<string, unknown> = {};
        for (let number = 2; number <= 8; number += 1) {
            const field = `notes${number}`;
            noteValues[field] = sourceDocument?.[field] ?? row?.[field];
        }
        result.push({
            row,
            orderId,
            date: extractDeliveryDate(row),
            address: getAddress(row),
            driver: String(row.orderDriver || row.driverName || row.agent || '').trim(),
            sourceDocument,
            sourceType,
            sourceNumber: String(sourceDocument?.id ?? orderId),
            sourceDate,
            noteValues,
            automaticBoxes: automatic.value,
            automaticWarning: automatic.warning,
            checked: true,
            boxSource: 'auto',
            sourceOverride: false,
            boxes: automatic.value,
            warning: automatic.warning,
        });
        return result;
    }, []);
}

function resolveSourceValue(row: DialogRouteRow, source: BoxSource): {boxes: number | null; warning?: string} {
    if (source === 'auto') return {boxes: row.automaticBoxes, warning: row.automaticWarning};
    if (source === 'manual') {
        const boxes = parseNumericNote(row.boxes);
        return {
            boxes,
            warning: boxes == null ? 'יש להזין מספר ארגזים ידני תקין.' : undefined,
        };
    }
    const boxes = parseNumericNote(row.noteValues[source]);
    return {
        boxes,
        warning: boxes == null ? `הערך בשדה ${source} חסר או אינו מספר תקין. ניתן לתקן ידנית.` : undefined,
    };
}

const DriverLinkDialog = ({open, rows, onClose}: DriverLinkDialogProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const noteFields = React.useMemo(() => getOrderExtraNoteFields(), []);
    const editableNoteFields = React.useMemo(
        () => Array.from({length: 7}, (_, index) => {
            const id = `notes${index + 2}`;
            return noteFields.find((field) => field.id === id) ?? {id, label: id};
        }),
        [noteFields],
    );
    const [routeRows, setRouteRows] = React.useState<DialogRouteRow[]>([]);
    const [globalSource, setGlobalSource] = React.useState<BoxSource>('auto');
    const [permissions, setPermissions] = React.useState(DEFAULT_PERMISSIONS);
    const [editableFields, setEditableFields] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [url, setUrl] = React.useState('');
    const [error, setError] = React.useState('');
    const [copied, setCopied] = React.useState(false);
    const linkInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (!open) return;
        setRouteRows(initializeRows(rows));
        setGlobalSource('auto');
        setPermissions(DEFAULT_PERMISSIONS);
        setEditableFields([]);
        setUrl('');
        setError('');
        setCopied(false);
    }, [open, rows]);

    const sourceOptions = React.useMemo(() => [
        {value: 'auto' as BoxSource, label: 'אוטומטי — אומדן קרטונים תקני'},
        ...editableNoteFields.map((field) => ({value: field.id as BoxSource, label: field.label})),
        {value: 'manual' as BoxSource, label: 'ידני'},
    ], [editableNoteFields]);

    const groups = React.useMemo(
        () => groupRowsByDeliveryDate(routeRows, (row) => row.date),
        [routeRows],
    );
    const checkedRows = routeRows.filter((row) => row.checked);
    const invalidChecked = checkedRows.some((row) => !row.date || parseNumericNote(row.boxes) == null);
    const canGenerate = checkedRows.length > 0 && !invalidChecked && !loading;
    const grandTotal = checkedRows.reduce((sum, row) => sum + (parseNumericNote(row.boxes) ?? 0), 0);
    const expiryDate = checkedRows
        .map((row) => row.date)
        .filter((date): date is string => Boolean(date))
        .sort()
        .pop();

    const changeGlobalSource = (source: BoxSource) => {
        setGlobalSource(source);
        setUrl('');
        setRouteRows((current) => current.map((row) => {
            const resolved = resolveSourceValue(row, source);
            return {
                ...row,
                boxSource: source,
                sourceOverride: false,
                boxes: resolved.boxes,
                warning: resolved.warning,
            };
        }));
    };

    const changeRowSource = (orderId: string, source: BoxSource) => {
        setUrl('');
        setRouteRows((current) => current.map((row) => {
            if (row.orderId !== orderId) return row;
            const resolved = resolveSourceValue(row, source);
            return {
                ...row,
                boxSource: source,
                sourceOverride: source !== globalSource,
                boxes: resolved.boxes,
                warning: resolved.warning,
            };
        }));
    };

    const changeBoxes = (orderId: string, raw: string) => {
        const boxes = raw === '' ? null : parseNumericNote(raw);
        setUrl('');
        setRouteRows((current) => current.map((row) => row.orderId === orderId
            ? {
                ...row,
                boxes,
                boxSource: 'manual',
                sourceOverride: true,
                warning: boxes == null ? 'יש להזין מספר ארגזים לא שלילי.' : undefined,
            }
            : row));
    };

    const generate = async () => {
        if (!canGenerate) return;
        setLoading(true);
        setError('');
        try {
            const payload = createDriverLinkRequestPayload(routeRows.map((row) => ({
                checked: row.checked,
                orderId: row.orderId,
                date: row.date ?? '',
                boxes: row.boxes,
                boxSource: row.boxSource,
                sourceOverride: row.sourceOverride,
                editableFields: Array.from(new Set([
                    ...editableFields,
                    ...(row.boxSource.startsWith('notes') ? [row.boxSource] : []),
                ])),
                editableFieldLabels: Object.fromEntries(
                    editableNoteFields.map((field) => [field.id, field.label]),
                ),
            })), permissions | PERM.SEE);
            const generatedUrl = await createDriverLink({
                mode: payload.mode,
                perm: payload.perm,
                orderIds: payload.orderIds,
                routeStops: payload.routeStops,
            });
            setUrl(generatedUrl);
        } catch (e: any) {
            setError(e?.message || 'יצירת הקישור נכשלה.');
        } finally {
            setLoading(false);
        }
    };

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(url);
        } catch {
            linkInputRef.current?.select();
            document.execCommand('copy');
        }
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="lg"
            PaperProps={{dir: 'rtl'}}
        >
            <DialogTitle>יצירת קישור נהג למסלול</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    {!routeRows.length && (
                        <Alert severity="info">לא נמצאו הזמנות תקינות בשורות המסומנות.</Alert>
                    )}

                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>הרשאות נהג</Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', columnGap: 2}}>
                            <FormControlLabel control={<Switch checked disabled />} label="צפייה" />
                            {PERMISSION_OPTIONS.map((option) => (
                                <FormControlLabel
                                    key={option.bit}
                                    control={(
                                        <Switch
                                            checked={Boolean(permissions & option.bit)}
                                            onChange={() => {
                                                setUrl('');
                                                setPermissions((current) => current & option.bit
                                                    ? current & ~option.bit
                                                    : current | option.bit);
                                            }}
                                        />
                                    )}
                                    label={option.label}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                            שדות שהנהג רשאי לערוך
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                            הערכים נשמרים ישירות במסמך המוביל (חשבונית, תעודת משלוח או הזמנה), ורק כאשר הרשאת עריכה פעילה.
                        </Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                            {editableNoteFields.map((field) => (
                                    <FormControlLabel
                                        key={field.id}
                                        control={(
                                            <Checkbox
                                                checked={editableFields.includes(field.id)}
                                                disabled={!Boolean(permissions & PERM.EDIT)}
                                                onChange={(event) => setEditableFields((current) =>
                                                    event.target.checked
                                                        ? [...current, field.id]
                                                        : current.filter((item) => item !== field.id))}
                                            />
                                        )}
                                        label={field.label}
                                    />
                            ))}
                        </Box>
                    </Box>

                    <TextField
                        select
                        size="small"
                        label="מקור ארגזים לכל המסלול"
                        value={globalSource}
                        onChange={(event) => changeGlobalSource(event.target.value as BoxSource)}
                        sx={{maxWidth: 420}}
                    >
                        {sourceOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                    </TextField>

                    {groups.map((group) => {
                        const total = group.rows
                            .filter((row) => row.checked)
                            .reduce((sum, row) => sum + (parseNumericNote(row.boxes) ?? 0), 0);
                        return (
                            <Box key={group.date ?? 'missing'} sx={{border: '1px solid', borderColor: 'divider', borderRadius: 2}}>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    sx={{px: 2, py: 1, bgcolor: 'grey.50'}}
                                >
                                    <Typography fontWeight={700}>תאריך אספקה: {formatDate(group.date)}</Typography>
                                    <Chip size="small" label={`סה״כ ${roundDecimals(total)} ארגזים`} />
                                </Stack>
                                <Divider />
                                {group.rows.map((routeRow, index) => (
                                    <Box key={routeRow.orderId}>
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: {xs: 'auto 1fr', md: 'auto minmax(170px, 1.3fr) minmax(150px, 1fr) minmax(170px, 1fr) 120px'},
                                                gap: 1.25,
                                                alignItems: 'center',
                                                p: 1.5,
                                            }}
                                        >
                                            <Checkbox
                                                checked={routeRow.checked}
                                                onChange={(event) => {
                                                    setUrl('');
                                                    setRouteRows((current) => current.map((row) => row.orderId === routeRow.orderId
                                                        ? {...row, checked: event.target.checked}
                                                        : row));
                                                }}
                                                inputProps={{'aria-label': `בחר הזמנה ${routeRow.orderId}`}}
                                            />
                                            <Box>
                                                <Typography fontWeight={700}>{routeRow.row.customer || 'לקוח ללא שם'}</Typography>
                                                <Typography variant="body2" color="text.secondary">{routeRow.address || 'כתובת חסרה'}</Typography>
                                                <Typography variant="caption">
                                                    הזמנה {routeRow.orderId}
                                                    {routeRow.driver ? ` · נהג: ${routeRow.driver}` : ' · נהג חסר'}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Chip
                                                    size="small"
                                                    color={routeRow.sourceType === 'order' ? 'default' : 'primary'}
                                                    label={`${sourceTypeLabel(routeRow.sourceType)} ${routeRow.sourceNumber}`}
                                                />
                                                <Typography variant="caption" display="block" sx={{mt: 0.5}}>
                                                    מקור כמויות · {formatDate(routeRow.sourceDate)}
                                                </Typography>
                                            </Box>
                                            <TextField
                                                select
                                                fullWidth
                                                size="small"
                                                label="מקור ארגזים"
                                                value={routeRow.boxSource}
                                                onChange={(event) => changeRowSource(routeRow.orderId, event.target.value as BoxSource)}
                                            >
                                                {sourceOptions.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                                ))}
                                            </TextField>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="ארגזים"
                                                value={routeRow.boxes ?? ''}
                                                onChange={(event) => changeBoxes(routeRow.orderId, event.target.value)}
                                                inputProps={{min: 0, step: 'any'}}
                                                error={routeRow.checked && parseNumericNote(routeRow.boxes) == null}
                                            />
                                            {(routeRow.warning || !routeRow.date || !routeRow.address || !routeRow.driver) && (
                                                <Alert
                                                    severity="warning"
                                                    icon={<WarningAmberIcon fontSize="small" />}
                                                    sx={{gridColumn: {xs: '1 / -1', md: '2 / -1'}, py: 0}}
                                                >
                                                    {[
                                                        routeRow.warning,
                                                        !routeRow.date ? 'תאריך אספקה חסר או לא תקין.' : '',
                                                        !routeRow.address ? 'כתובת חסרה.' : '',
                                                        !routeRow.driver ? 'נהג חסר.' : '',
                                                    ].filter(Boolean).join(' ')}
                                                </Alert>
                                            )}
                                        </Box>
                                        {index < group.rows.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>
                        );
                    })}

                    {!!checkedRows.length && (
                        <Alert severity={invalidChecked ? 'warning' : 'info'}>
                            {invalidChecked
                                ? 'יש לתקן תאריך או מספר ארגזים לפני יצירת הקישור.'
                                : `סה״כ ${roundDecimals(grandTotal)} ארגזים ב־${checkedRows.length} עצירות. תוקף הקישור עד ${expiryDate ? formatIsraelExpiry(expiryDate) : ''}.`}
                        </Alert>
                    )}

                    {error && <Alert severity="error">{error}</Alert>}
                    {url && (
                        <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                                inputRef={linkInputRef}
                                fullWidth
                                size="small"
                                value={url}
                                InputProps={{readOnly: true}}
                                inputProps={{dir: 'ltr'}}
                                label="קישור נהג"
                            />
                            <Tooltip title={copied ? 'הועתק' : 'העתק'}>
                                <IconButton onClick={copyLink} color={copied ? 'success' : 'default'}>
                                    <ContentCopyIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="פתח">
                                <IconButton onClick={() => window.open(url, '_blank')?.focus()}>
                                    <OpenInNewIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>סגור</Button>
                <Button
                    variant="contained"
                    onClick={generate}
                    disabled={!canGenerate}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    {loading ? 'יוצר קישור…' : 'צור קישור'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DriverLinkDialog;
